import { useCallback, useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import assetProcessingActivitiesApi from 'config/api/assetProcessingActivities/assetProcessingActivities';
import assetRopaApi from 'config/api/assetRopa/assetRopa';
import type { AssetProcessingActivityDetail } from 'config/api/assetRopa/assetRopa.types';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import useUnsavedChangesProtection from 'hooks/useUnsavedChangesProtection/useUnsavedChangesProtection';
import type { AssetProcessingApaFormValues } from 'pages/ProcessingActivities/_shared/assetProcessingApa.types';

import {
  buildCountryHandlingDiff,
  expandCountryRowsToHandlings,
  mapDetailToFormValues,
  mapPurposeApplicationToUpdateInput,
} from './assetProcessingActivityEditor.helpers';

type UseAssetProcessingActivityEditorOptions = {
  apaId: number;
  assetRopaId: number;
  initialEditMode?: boolean;
  onNavigate: () => void;
  onSaveSuccess?: (detail: AssetProcessingActivityDetail, closeAfterSave: boolean) => void;
  onSubmitSuccess?: (detail: AssetProcessingActivityDetail) => void;
  organizationId?: number;
};

const persistApaNestedFields = async (
  organizationId: number,
  apaId: number,
  formValues: AssetProcessingApaFormValues,
  existingDetail: AssetProcessingActivityDetail,
) => {
  await Promise.all(
    formValues.purposeApplications.map(purpose =>
      assetProcessingActivitiesApi.updatePurpose(organizationId, apaId, purpose.id, mapPurposeApplicationToUpdateInput(purpose)),
    ),
  );

  const desiredCountries = expandCountryRowsToHandlings(formValues.countryRows);
  const { toCreate, toDeleteIds } = buildCountryHandlingDiff(existingDetail.countries, desiredCountries);

  await Promise.all(toDeleteIds.map(countryId => assetProcessingActivitiesApi.deleteCountry(organizationId, apaId, countryId)));
  await Promise.all(toCreate.map(payload => assetProcessingActivitiesApi.createCountry(organizationId, apaId, payload)));
};

const useAssetProcessingActivityEditor = ({
  apaId,
  initialEditMode = false,
  onNavigate,
  onSaveSuccess,
  onSubmitSuccess,
  organizationId,
}: UseAssetProcessingActivityEditorOptions) => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [formValues, setFormValues] = useState<AssetProcessingApaFormValues | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Partial<Record<string, string>>>({});

  const detailQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_APA_DETAIL, organizationId, apaId],
    async () => {
      const { data } = await assetRopaApi.getProcessingActivity(organizationId as number, apaId);
      return data;
    },
    {
      enabled: Boolean(organizationId),
      retry: false,
    },
  );

  useEffect(() => {
    if (detailQuery.data) {
      setFormValues(mapDetailToFormValues(detailQuery.data));
      setIsDirty(false);
      setValidationErrors({});
    }
  }, [detailQuery.data]);

  const handleFormChange = useCallback((nextValues: AssetProcessingApaFormValues) => {
    setFormValues(nextValues);
    setIsDirty(true);
    setValidationErrors({});
  }, []);

  const saveMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_APA_UPDATE,
    async (closeAfterSave: boolean) => {
      if (!formValues || !detailQuery.data || !organizationId) return null;

      await assetRopaApi.updateProcessingActivity(organizationId, apaId, {
        dpiaIndicationStatus: formValues.dpiaIndicationStatus as AssetProcessingActivityDetail['dpiaIndicationStatus'],
        dpiaIndicationComment: formValues.dpiaIndicationComment,
        minimumSecurityAssessment: formValues.minimumSecurityAssessment as AssetProcessingActivityDetail['minimumSecurityAssessment'],
        minimumSecurityAssessmentComment: formValues.minimumSecurityAssessmentComment,
        updatedAt: detailQuery.data.updatedAt,
        revision: detailQuery.data.revision,
      });

      await persistApaNestedFields(organizationId, apaId, formValues, detailQuery.data);

      const { data } = await assetRopaApi.getProcessingActivity(organizationId, apaId);
      return { closeAfterSave, data };
    },
    {
      onSuccess: result => {
        if (!result) return;

        queryClient.setQueryData([QUERY_KEYS.ASSET_ROPA_APA_DETAIL, organizationId, apaId], result.data);
        queryClient.invalidateQueries([QUERY_KEYS.ASSET_ROPA_WORKSPACE, organizationId]);
        queryClient.invalidateQueries([QUERY_KEYS.ASSET_ROPA_MINE, organizationId]);
        setFormValues(mapDetailToFormValues(result.data));
        setIsDirty(false);
        onSaveSuccess?.(result.data, result.closeAfterSave);
      },
    },
  );

  const submitMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_APA_SUBMIT,
    async () => {
      if (!detailQuery.data || !organizationId) return null;

      const { data } = await assetRopaApi.submitProcessingActivity(organizationId, apaId, {
        updatedAt: detailQuery.data.updatedAt,
        revision: detailQuery.data.revision,
      });

      return data;
    },
    {
      onSuccess: data => {
        if (!data) return;

        queryClient.setQueryData([QUERY_KEYS.ASSET_ROPA_APA_DETAIL, organizationId, apaId], data);
        queryClient.invalidateQueries([QUERY_KEYS.ASSET_ROPA_WORKSPACE, organizationId]);
        queryClient.invalidateQueries([QUERY_KEYS.ASSET_ROPA_MINE, organizationId]);
        setIsDirty(false);
        onSubmitSuccess?.(data);
      },
    },
  );

  const handleSubmit = useCallback(async () => {
    if (isDirty) {
      await saveMutation.mutateAsync(false);
    }

    await submitMutation.mutateAsync();
  }, [isDirty, saveMutation, submitMutation]);

  const { NavigationPrompt, handleNavigation } = useUnsavedChangesProtection({
    enabled: isEditing,
    isDirty,
    onNavigate,
    onSave: async () => {
      await saveMutation.mutateAsync(false);
    },
  });

  const readOnly = !isEditing || detailQuery.data?.review?.status === 'submitted' || detailQuery.data?.review?.status === 'accepted';

  return {
    detail: detailQuery.data,
    formValues,
    handleFormChange,
    handleNavigation,
    handleSubmit,
    isDirty,
    isEditing,
    isError: detailQuery.isError,
    isLoading: detailQuery.isLoading,
    NavigationPrompt,
    readOnly,
    refetch: detailQuery.refetch,
    saveMutation,
    setIsEditing,
    setValidationErrors,
    submitMutation,
    validationErrors,
  };
};

export default useAssetProcessingActivityEditor;
